"use client";
import type { ImageAssetFragment } from "@commerce-frontend/types";
import { captureMessage } from "@sentry/nextjs";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { useMemo } from "react";
import { InstantSearch } from "react-instantsearch";
import { useEnvVar } from "~/lib/config/context";
import type { StoreMetadata } from "~/lib/store-config/context";
import { useMenu } from "../MenuContext";
import { SearchBar } from "./SearchBar/SearchBar";
import { SearchModal } from "./SearchModal/SearchModal";

type Props = {
	logo?: ImageAssetFragment;
	isScrolled: boolean;
} & Pick<StoreMetadata, "siteName">;

export const Search = ({ logo, isScrolled, siteName }: Props) => {
	const { searchOpen } = useMenu();
	const indexName = useEnvVar("NEXT_PUBLIC_ALGOLIA_STORES_INDEX") ?? "";
	const appId = useEnvVar("NEXT_PUBLIC_ALGOLIA_APP_ID") ?? "";
	const searchApiKey = useEnvVar("NEXT_PUBLIC_ALGOLIA_AUTOCOMPLETE_API_KEY") ?? "";

	if (!appId || !searchApiKey) {
		captureMessage("Search: Algolia appId or searchApiKey is not set");
		return null;
	}

	const searchClient = useMemo(() => {
		const algoliaClient = algoliasearch(appId, searchApiKey);

		return {
			...algoliaClient,
			// Bypass Algolia's API call if the query is too short
			// https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/react/#implementing-a-proxy
			search<TObject>(requests: any) {
				const isShortQuery = requests.every(({ params }: any) => {
					return !params.query || params.query.trim().length < 2;
				});

				if (isShortQuery) {
					return Promise.resolve({
						results: requests.map(() => ({
							hits: [],
							nbHits: 0,
							nbPages: 0,
							page: 0,
							processingTimeMS: 0,
							query: "",
							params: "",
						})),
					});
				}

				return algoliaClient.search<TObject>(requests);
			},
		};
	}, [appId, searchApiKey]);

	return (
		<InstantSearch indexName={indexName} searchClient={searchClient}>
			{/* The default search bar rendered (initial state) */}
			{!searchOpen ? <SearchBar /> : null}

			{/* After the search input is clicked, a modal opens with a new search bar that is focused, including the Algolia search results */}
			<SearchModal logo={logo} isScrolled={isScrolled} siteName={siteName} />
		</InstantSearch>
	);
};
